import { cn } from '@/lib/utils';

/** Circular percentage ring for the Day-1 setup surfaces (IX-3929).
 *
 * A conic gradient with the card colour punched out of the middle (IX-4114) —
 * the shape the design uses on the sidebar chip, the setup panel header and the
 * Home card. The three sizes match the design's three call sites.
 *
 * The label is a notch tighter than the design's raw values: the prototype is
 * set in DM Sans, the backoffice renders Inter, and at the small size Inter's
 * digits are wide enough that "86%" spills over the ring — "100%" more so.
 * `tabular-nums` also stops the label shifting as the count climbs. */
const SIZES = {
    sm: { box: 'size-[30px]', hole: 'inset-[2.5px]', text: 'text-[9px]' },
    md: { box: 'size-[38px]', hole: 'inset-[4px]', text: 'text-[11px]' },
    lg: { box: 'size-[46px]', hole: 'inset-[5px]', text: 'text-[13px]' },
} as const;

export function ProgressRing({ pct, size = 'md' }: { pct: number; size?: keyof typeof SIZES }) {
    const clamped = Math.min(100, Math.max(0, pct));
    const { box, hole, text } = SIZES[size];
    return (
        <div className={cn('relative flex-none', box)}>
            <div
                className="absolute inset-0 rounded-full transition-[background] duration-500"
                style={{
                    background: `conic-gradient(var(--accent-foreground) ${clamped * 3.6}deg, var(--border) 0deg)`,
                }}
            />
            <div
                className={cn(
                    'absolute flex items-center justify-center rounded-full bg-card font-bold leading-none tracking-tight tabular-nums text-foreground',
                    hole,
                    text
                )}
            >
                {pct}
            </div>
        </div>
    );
}
